import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ScoreBreakdown from '../components/ScoreBreakdown';
import DischargeBreakdown from '../components/DischargeBreakdown';
import { api } from '../lib/api';
import { useScoring } from '../lib/scoring';
import {
  LargeTitle, Section, Card, ScoreHero, MonthChips, MetricTile, PageSkeleton, scoreBand,
} from '../components/ui';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Every month from the first scored one through this month, so months without
// a CScore yet (like the one in progress) can still be opened
function monthsToNow(chartData) {
  if (chartData.length === 0) return [];
  const now = new Date();
  const out = [];
  let { month, year } = chartData[0];
  while (year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth() + 1)) {
    out.push({ month, year });
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return out;
}

function ClinicianView({ clinician, chartData, month, year, onSelect, monthData, loadingMonth, weights, back }) {
  const band = monthData?.cscore ? scoreBand(monthData.cscore) : null;
  return (
    <div>
      <LargeTitle back={back} title={clinician.full_name} subtitle={clinician.kipu_clinician_id ? `Clinician ID ${clinician.kipu_clinician_id}` : null} />
      <ScoreHero label="CScore" data={chartData} focusMonth={month} focusYear={year} />

      <Section title="Month details">
        <MonthChips months={monthsToNow(chartData)} month={month} year={year} onSelect={onSelect} />
        {!month ? (
          <Card className="p-6 mt-3 text-center">
            <p className="text-[15px] font-semibold text-[#111]">No scored months yet</p>
            <p className="text-[13px] text-gray-500 mt-1">Details show here once KIPU data comes in.</p>
          </Card>
        ) : loadingMonth ? (
          <PageSkeleton hero={false} rows={2} />
        ) : monthData ? (
          <>
            <Card className="p-4 mt-3 flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-gray-500">CScore for {MONTHS[month - 1]} {year}</p>
                <p className="text-[34px] leading-[40px] font-bold tabular-nums tracking-[-0.02em] mt-0.5" style={{ color: band?.text }}>
                  {monthData.cscore || 0}
                </p>
              </div>
              {band && (
                <span className="px-3 py-1.5 rounded-full text-[13px] font-semibold" style={{ backgroundColor: `${band.color}1A`, color: band.text }}>
                  {band.label}
                </span>
              )}
            </Card>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <MetricTile label="Caseload" value={monthData.caseload || 0} hint="average cases" />
              <MetricTile label="Discharges" value={monthData.discharges || 0} />
              <MetricTile label="Length of stay" value={monthData.length_of_stay || 0} hint="average days" />
              <MetricTile
                label="Attendance"
                value={monthData.attendance_pct ? `${(monthData.attendance_pct * 100).toFixed(1)}%` : '0%'}
              />
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-3 md:items-start">
            <Card className="p-4 md:p-5 mt-3">
              <ScoreBreakdown breakdown={monthData} score={monthData.cscore} weights={weights} title="How this score is built" />
            </Card>
            {monthData.discharges > 0 && (
              <Card className="p-4 md:p-5 mt-3">
                <DischargeBreakdown record={monthData} />
              </Card>
            )}
            </div>
          </>
        ) : (
          <Card className="p-6 mt-3 text-center">
            <p className="text-[15px] font-semibold text-[#111]">No details for {MONTHS[month - 1]} {year} yet</p>
            <p className="text-[13px] text-gray-500 mt-1">Pick another month above.</p>
          </Card>
        )}
      </Section>
    </div>
  );
}

export default function ClinicianDetail() {
  const { clinicianId } = useParams();
  const { weights } = useScoring();
  const [searchParams] = useSearchParams();
  const fromClinicId = searchParams.get('clinicId');
  const fromClinicName = searchParams.get('clinicName');
  const [clinician, setClinician] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load clinician and chart data
  useEffect(() => {
    Promise.all([
      api.clinicians.get(clinicianId),
      api.clinicians.chart(clinicianId),
    ])
      .then(([clinicianData, chart]) => {
        setClinician(clinicianData);
        setChartData(chart);
        // Default to the latest month/year
        if (chart.length > 0) {
          const latest = chart[chart.length - 1];
          setSelectedMonth(latest.month);
          setSelectedYear(latest.year);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicianId]);

  // Load month data when selection changes
  useEffect(() => {
    if (!selectedMonth || !selectedYear) return undefined;
    // Clear the old month first and ignore late replies, so a heading never
    // sits over another month's numbers
    let current = true;
    setMonthData(null);
    setLoadingMonth(true);
    api.clinicians.records(clinicianId, selectedMonth, selectedYear)
      .then(records => { if (current) setMonthData(records[0] || null); })
      .catch(console.error)
      .finally(() => { if (current) setLoadingMonth(false); });
    return () => { current = false; };
  }, [clinicianId, selectedMonth, selectedYear]);

  if (loading || !clinician) return <PageSkeleton rows={4} />;

  return (
    <ClinicianView
      clinician={clinician}
      chartData={chartData}
      month={selectedMonth}
      year={selectedYear}
      onSelect={(m, y) => { setSelectedMonth(m); setSelectedYear(y); }}
      monthData={monthData}
      loadingMonth={loadingMonth}
      weights={weights}
      back={fromClinicId
        ? { to: `/clinics/${fromClinicId}`, label: fromClinicName || 'Clinic' }
        : { to: '/clinicians', label: 'Clinicians' }}
    />
  );
}
